import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Row,
  Col,
  Label,
  Input,
  Button,
  Spinner
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { crearTicket } from '../../slices/ticketsSlice';
import { getsolicitud, getFarmaciaPorCodigo } from '../../helpers/backend_helper';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

// Registrar plugins de FilePond
registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileEncode,
  FilePondPluginFileValidateType
);

const SoporteModal = ({ isOpen, onClose, userData }) => {
  const dispatch = useDispatch();
  const { creandoTicket } = useSelector(state => state.Tickets);
  
  // Debug: Verificar datos del usuario
  React.useEffect(() => {
    if (isOpen && userData) {
      console.log('🔍 SoporteModal - userData:', userData);
      console.log('🔍 SoporteModal - farmacia:', userData?.farmacia);
      console.log('🔍 SoporteModal - perfil:', userData?.perfil);
    }
  }, [isOpen, userData]);
  
  // Detectar si estamos en una página de solicitud y extraer el ID
  const getSolicitudIdFromUrl = () => {
    const url = window.location.href;
    // Patrones comunes de URLs de solicitud
    const solicitudMatch = url.match(/solicitud(?:es)?\/(?:ver\/)?([^\/]+)/);
    if (solicitudMatch && solicitudMatch[1] && solicitudMatch[1] !== 'nueva' && solicitudMatch[1] !== 'listar') {
      return solicitudMatch[1];
    }
    return null;
  };

  // Obtener codigo_proveedor, farmacia_id, farmacia_nombre e identificador de la solicitud
  const getSolicitudData = async () => {
    const solicitudId = getSolicitudIdFromUrl();
    if (!solicitudId) return { codigo_proveedor: null, farmacia_id: null, farmacia_nombre: null, solicitud_identificador: null };
    
    try {
      const response = await getsolicitud(solicitudId);
      const solicitud = response.data?.data || response.data;
      
      if (solicitud) {
        const codigoProveedor = solicitud.codigoProveedor || null;
        const solicitudIdentificador = solicitud.identificador || null;
        
        // Si hay codigoProveedor, obtener la farmacia completa
        let farmaciaId = null;
        let farmaciaNombre = null;
        
        if (codigoProveedor && (userData?.perfil === 'Administrador' || userData?.perfil === 'MyHealth')) {
          try {
            const farmaciaResponse = await getFarmaciaPorCodigo(codigoProveedor);
            const farmacia = farmaciaResponse.data?.data || farmaciaResponse.data;
            
            if (farmacia) {
              farmaciaId = farmacia.id || null;
              farmaciaNombre = farmacia.nombre || null;
            }
          } catch (farmaciaError) {
            console.error('Error al obtener farmacia:', farmaciaError);
          }
        }
        
        return { 
          codigo_proveedor: codigoProveedor,
          farmacia_id: farmaciaId,
          farmacia_nombre: farmaciaNombre,
          solicitud_identificador: solicitudIdentificador
        };
      }
    } catch (error) {
      console.error('Error al obtener datos de la solicitud:', error);
    }
    
    return { codigo_proveedor: null, farmacia_id: null, farmacia_nombre: null, solicitud_identificador: null };
  };

  // Estado del formulario
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    categoria: userData?.perfil === 'MyHealth' ? 'Información' : 'Soporte',
    prioridad: 'Media',
    url_actual: window.location.href,
    usuario_id: userData?.id || '',
    usuario_nombre: `${userData?.nombre || ''} ${userData?.apellido || ''}`.trim(),
    usuario_email: userData?.correo || '',
    usuario_telefono: userData?.telefono || '',
    farmacia_id: userData?.farmacia || null,
    codigo_proveedor: null
  });
  
  // Estado para identificador de solicitud (para título automático)
  const [solicitudInfo, setSolicitudInfo] = useState({
    esPaginaSolicitud: false,
    identificador: null
  });
  
  // Estado para archivos
  const [files, setFiles] = useState([]);
  
  // Estado para validación
  const [errors, setErrors] = useState({});
  
  // useEffect para detectar si estamos en página de solicitud y generar título automático
  useEffect(() => {
    const detectarSolicitudYGenerarTitulo = async () => {
      if (isOpen && userData) {
        const isAdminOrMyHealth = userData?.perfil === 'Administrador' || userData?.perfil === 'MyHealth';
        
        if (isAdminOrMyHealth) {
          const solicitudId = getSolicitudIdFromUrl();
          if (solicitudId) {
            try {
              const response = await getsolicitud(solicitudId);
              const solicitud = response.data?.data || response.data;
              
              if (solicitud && solicitud.identificador) {
                const tituloAuto = `Solicitud #${solicitud.identificador} - Información`;
                setSolicitudInfo({
                  esPaginaSolicitud: true,
                  identificador: solicitud.identificador
                });
                setFormData(prev => ({
                  ...prev,
                  titulo: tituloAuto
                }));
              }
            } catch (error) {
              console.error('Error al obtener solicitud para título:', error);
            }
          }
        }
      }
    };
    
    detectarSolicitudYGenerarTitulo();
  }, [isOpen, userData]);
  
  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Validar formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.titulo.trim()) {
      newErrors.titulo = 'El título es requerido';
    } else if (formData.titulo.trim().length < 5) {
      newErrors.titulo = 'El título debe tener al menos 5 caracteres';
    }
    
    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    } else if (formData.descripcion.trim().length < 10) {
      newErrors.descripcion = 'La descripción debe tener al menos 10 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Determinar si debemos incluir codigo_proveedor y farmacia_id
      const isAdminOrMyHealth = userData?.perfil === 'Administrador' || userData?.perfil === 'MyHealth';
      const solicitudData = isAdminOrMyHealth ? await getSolicitudData() : { codigo_proveedor: null, farmacia_id: null, farmacia_nombre: null, solicitud_identificador: null };
      
      // Generar título automático si estamos en página de solicitud y no hay título personalizado
      let tituloFinal = formData.titulo;
      if (solicitudData.solicitud_identificador && !formData.titulo.trim()) {
        tituloFinal = `Solicitud #${solicitudData.solicitud_identificador}`;
      }
      
      // Agregar información de contacto a la descripción
      const contactInfo = `\n\nInformación de contacto:\nUsuario: ${formData.usuario_nombre}\nEmail: ${formData.usuario_email}\nTeléfono: ${formData.usuario_telefono || 'No especificado'}\nPágina actual: ${window.location.href}`;
      
      const ticketDataToSend = {
        ...formData,
        titulo: tituloFinal,
        descripcion: formData.descripcion + contactInfo,
        codigo_proveedor: solicitudData.codigo_proveedor,
        farmacia_id: solicitudData.farmacia_id || formData.farmacia_id,
        farmacia_nombre: solicitudData.farmacia_nombre || formData.farmacia_nombre
      };
      
      await dispatch(crearTicket({
        ticketData: ticketDataToSend,
        archivos: files
      })).unwrap();
      
      // Cerrar modal y limpiar formulario
      onClose();
      resetForm();
      
    } catch (error) {
      console.error('Error al crear ticket:', error);
    }
  };
  
  // Resetear formulario
  const resetForm = () => {
    setFormData({
      titulo: '',
      descripcion: '',
      categoria: userData?.perfil === 'MyHealth' ? 'Información' : 'Soporte',
      prioridad: 'Media',
      url_actual: window.location.href,
      usuario_id: userData?.id || '',
      usuario_nombre: `${userData?.nombre || ''} ${userData?.apellido || ''}`.trim(),
      usuario_email: userData?.correo || '',
      usuario_telefono: userData?.telefono || '',
      farmacia_id: userData?.farmacia || null,
      codigo_proveedor: null
    });
    setSolicitudInfo({
      esPaginaSolicitud: false,
      identificador: null
    });
    setFiles([]);
    setErrors({});
  };
  
  // Manejar cierre del modal
  const handleClose = () => {
    if (!creandoTicket) {
      onClose();
      resetForm();
    }
  };
  
  return (
    <Modal 
      isOpen={isOpen} 
      toggle={handleClose} 
      size="lg" 
      centered
      backdrop="static"
      keyboard={!creandoTicket}
    >
      <ModalHeader toggle={handleClose}>
        <i className="ri-headphone-line me-2 text-primary"></i>
        Crear Ticket de Soporte
      </ModalHeader>
      
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <Label for="categoria" className="form-label">
                <i className="ri-apps-line me-1"></i>
                Categoría
              </Label>
              <Input 
                type="select" 
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                className={errors.categoria ? 'is-invalid' : ''}
                disabled={creandoTicket}
              >
                <option value="Soporte">Soporte Técnico</option>
                <option value="Facturacion">Facturación</option>
                <option value="Información">Información</option>
                <option value="Auditor">Auditor</option>
              </Input>
              {errors.categoria && <div className="invalid-feedback">{errors.categoria}</div>}
            </Col>
            
            <Col md={6}>
              <Label for="prioridad" className="form-label">
                <i className="ri-flag-line me-1"></i>
                Prioridad
              </Label>
              <Input 
                type="select" 
                name="prioridad"
                value={formData.prioridad}
                onChange={handleChange}
                className={errors.prioridad ? 'is-invalid' : ''}
                disabled={creandoTicket}
              >
                <option value="Baja">Baja</option>
                <option value="Media">Media</option>
                <option value="Alta">Alta</option>
                <option value="Urgente">Urgente</option>
              </Input>
              {errors.prioridad && <div className="invalid-feedback">{errors.prioridad}</div>}
            </Col>
          </Row>
          
          <div className="mt-3">
            <Label for="titulo" className="form-label">
              <i className="ri-text me-1"></i>
              Título {solicitudInfo.esPaginaSolicitud && <span className="text-muted">(Auto-generado)</span>}
            </Label>
            <Input 
              type="text" 
              name="titulo"
              placeholder="Describe brevemente tu problema"
              value={formData.titulo}
              onChange={handleChange}
              className={errors.titulo ? 'is-invalid' : ''}
              disabled={creandoTicket || solicitudInfo.esPaginaSolicitud}
              readOnly={solicitudInfo.esPaginaSolicitud}
              maxLength={200}
            />
            {errors.titulo && <div className="invalid-feedback">{errors.titulo}</div>}
            <small className="text-muted">
              {solicitudInfo.esPaginaSolicitud 
                ? 'Título generado automáticamente desde la solicitud' 
                : 'Mínimo 5 caracteres'}
            </small>
          </div>
          
          <div className="mt-3">
            <Label for="descripcion" className="form-label">
              <i className="ri-file-text-line me-1"></i>
              Descripción detallada
            </Label>
            <Input 
              type="textarea" 
              name="descripcion"
              rows="4"
              placeholder="Explica en detalle el problema que estás experimentando..."
              value={formData.descripcion}
              onChange={handleChange}
              className={errors.descripcion ? 'is-invalid' : ''}
              disabled={creandoTicket}
            />
            {errors.descripcion && <div className="invalid-feedback">{errors.descripcion}</div>}
            <small className="text-muted">Mínimo 10 caracteres</small>
          </div>
          
          <div className="mt-3">
            <Label className="form-label">
              <i className="ri-attachment-line me-1"></i>
              Archivos adjuntos (opcional)
            </Label>
            <FilePond
              files={files}
              onupdatefiles={setFiles}
              allowMultiple={true}
              maxFiles={3}
              name="files"
              labelIdle='<i class="ri-upload-cloud-line fs-24 mb-2"></i><br/>Arrastra o <span class="filepond--label-action text-primary">selecciona</span> archivos'
              className="filepond filepond-input-multiple"
              allowFileEncode={true}
              acceptedFileTypes={["image/*", "application/pdf"]}
              labelFileTypeNotAllowed="Solo se permiten imágenes y PDF"
              fileValidateTypeLabelExpectedTypes="Seleccione una imagen (JPG, PNG, WEBP) o PDF"
              maxFileSize="10MB"
              credits={false}
              disabled={creandoTicket}
            />
            <small className="text-muted">
              Máximo 3 archivos, 10MB cada uno. Formatos: JPG, PNG, WEBP, PDF
            </small>
          </div>
          
          {/* Información del usuario (solo lectura) */}
          <div className="mt-3 p-3 bg-light rounded">
            <small className="text-muted">
              <strong>Información de contacto:</strong><br />
              Usuario: {formData.usuario_nombre}<br />
              Email: {formData.usuario_email}<br />
              Teléfono: {formData.usuario_telefono || 'No especificado'}<br />
              Página actual: {window.location.href}
            </small>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            color="secondary" 
            onClick={handleClose}
            disabled={creandoTicket}
          >
            <i className="ri-close-line me-1"></i>
            Cancelar
          </Button>
          <Button 
            color="primary" 
            type="submit"
            disabled={creandoTicket}
          >
            {creandoTicket ? (
              <>
                <Spinner size="sm" className="me-1" />
                Creando...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line me-1"></i>
                Enviar Ticket
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default SoporteModal;
